import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { Modal, SwipeableDrawer } from '@mui/material';
import { Check, DeleteOutline } from '@mui/icons-material';
import { clearStalePreviewMutations, retryPreviewSave, savePreview, updatePreviewList } from '../redux/actions/savedPreviewActions';
import { TIP_SAVED_LIST_PICKER_LEARNED_V1 } from '../constants/local_storage_constants';
import EmojiPickerModal from './EmojiPickerModal';
import NewSavedListButton from './NewSavedListButton';
import SigninButtonPopup from './SigninButtonPopup';
import StatusToast from './StatusToast';
import './SavedHighlights.css';

const hasLearnedListPicker = () => {
    try {
        return localStorage.getItem(TIP_SAVED_LIST_PICKER_LEARNED_V1) === 'true';
    } catch (_) {
        return false;
    }
};

export default function SavedHighlightDrawer() {
    const dispatch = useDispatch();
    const store = useStore();
    const { selection, memberships, mutations, notice, uid, session } = useSelector(state => state.savedHighlights);
    const { authenticated, savedLists } = useSelector(state => state.user);
    const preview = selection?.preview;
    const token = selection?.token;
    const [localError, setLocalError] = useState(null);
    const [showSaving, setShowSaving] = useState(false);
    const [toast, setToast] = useState(null);
    const [showEmoji, setShowEmoji] = useState(false);
    const [showTip, setShowTip] = useState(false);
    const [orderedIds, setOrderedIds] = useState([]);
    const started = useRef(null);
    const activeToken = useRef(token);
    activeToken.current = token;
    const mutation = preview ? mutations[preview.id] : null;
    const busy = Boolean(mutation?.pending);
    const startedAt = mutation?.startedAt;
    const error = mutation?.error?.message || localError;
    const isSaved = Boolean(preview && Object.prototype.hasOwnProperty.call(memberships, preview.id));
    const canManage = authenticated && Boolean(uid);
    const close = useCallback(() => dispatch({ type: 'CLOSE_SAVED_HIGHLIGHT', payload: { token } }), [dispatch, token]);
    const perform = useCallback(async (action, { removing = false } = {}) => {
        const captured = { token, uid, session };
        setLocalError(null);
        try {
            const record = await dispatch(action);
            const current = store.getState().savedHighlights;
            if (current.uid !== captured.uid || current.session !== captured.session) return;
            if (removing && !record) {
                if (activeToken.current === captured.token) close();
                setToast({ message: 'Removed highlight', key: Date.now() });
            }
        } catch (failure) {
            const current = store.getState().savedHighlights;
            if (current.uid !== captured.uid || current.session !== captured.session) return;
            // Queue failures are displayed from store state, including after dismissal.
            if (current.mutations[preview.id]?.error) return;
            if (activeToken.current === captured.token) setLocalError(failure.message);
            else setToast({ message: failure.message, key: Date.now() });
        }
    }, [close, dispatch, preview, session, store, token, uid]);

    useEffect(() => {
        setShowSaving(false);
        if (!busy) return undefined;
        const timer = window.setTimeout(() => setShowSaving(true), Math.max(0, 1000 - (Date.now() - startedAt)));
        return () => window.clearTimeout(timer);
    }, [busy, startedAt, token]);
    useEffect(() => {
        if (notice && notice.id !== preview?.id) {
            setToast(notice);
            dispatch({ type: 'SAVED_HIGHLIGHTS_NOTICE', payload: { uid, session, notice: null } });
        }
    }, [dispatch, notice, preview?.id, session, uid]);
    useEffect(() => {
        setToast(null);
        dispatch(clearStalePreviewMutations());
    }, [dispatch, uid, session]);

    useEffect(() => {
        setLocalError(null);
        setShowEmoji(false);
        setShowTip(!hasLearnedListPicker());
        setOrderedIds([]);
        started.current = null;
    }, [token]);
    useEffect(() => {
        if (!preview || !authenticated || !uid || started.current === token) return;
        started.current = token;
        const selected = new Set(memberships[preview.id] || []);
        setOrderedIds([...savedLists.filter(list => selected.has(list.id)), ...savedLists.filter(list => !selected.has(list.id))].map(list => list.id));
        if (!isSaved && !mutation?.error) perform(savePreview(preview));
    }, [authenticated, isSaved, memberships, mutation, perform, preview, savedLists, token, uid]);

    const changeList = (list, remove = false) => {
        try { localStorage.setItem(TIP_SAVED_LIST_PICKER_LEARNED_V1, 'true'); } catch (_) { /* Keep this opening learned. */ }
        setShowTip(false);
        perform(updatePreviewList(preview, { name: list.name, listId: list.pending ? undefined : list.id, remove }));
    };
    const lists = [...savedLists, ...(mutation?.newLists || []).filter(list => !savedLists.some(saved => saved.name === list.name))];
    const ordered = [...orderedIds.map(id => lists.find(list => list.id === id)).filter(Boolean), ...lists.filter(list => !orderedIds.includes(list.id))];
    return <>
        <StatusToast open={Boolean(toast)} instanceKey={toast?.key} message={toast?.message} onClose={() => setToast(null)} />
        <Modal open={Boolean(preview && (!authenticated || !uid))} onClose={close}>
            <div><SigninButtonPopup close={() => { if (!store.getState().user.authenticated) close(); }} /></div>
        </Modal>
        <SwipeableDrawer anchor='bottom' open={Boolean(preview && authenticated && uid)} onOpen={() => {}}
            onClose={close} disableDiscovery PaperProps={{ sx: { borderRadius: '18px 18px 0 0' } }}>
            {preview && <div className='savedListPickerDrawer horizontalCenter appDrawerSurface savedHighlightDrawer'>
                <div className='savedListPickerDragIndicator' aria-hidden='true' />
                <div className='savedListPickerSummary savedHighlightSummary'>
                    <img src={preview.source?.imageURL} alt='' className='saveSourceImage podcastArtwork' />
                    <div className='savedHighlightHeading'>
                        <div className='savedHighlightLabel'>{isSaved ? 'Saved highlight' : 'Save highlight'}</div>
                        <div className='saveTrackTitle'>{preview.title}</div>
                        {showSaving && busy && <div className='savedHighlightSaving' role='status'>Saving…</div>}
                    </div>
                    <button type='button' aria-label='Remove highlight from saved' disabled={!canManage}
                        onClick={() => perform(savePreview(preview, false), { removing: true })}>
                        <DeleteOutline aria-hidden='true' />
                    </button>
                </div>
                <div className='savedListPickerBody'>
                    {error && <div className='savedHighlightError' role='alert'>{error}
                        {mutation?.error && <button type='button' onClick={() => perform(retryPreviewSave(preview), { removing: mutation.error.kind === 'remove' })}>Retry</button>}
                    </div>}
                    {ordered.length === 0 ? <div className='savedListPickerEmptyState'>
                        <div className='savedListPickerEmptyTitle'>No lists yet.</div>Create a list to organize this highlight.
                    </div> : showTip && <p className='savedListPickerInstruction'>Tap an emoji to add or remove this highlight from a list.</p>}
                    <div className='savedListsGrid'>
                        {ordered.map(list => {
                            const selected = (mutation?.selectedListIds || memberships[preview.id] || []).includes(list.id);
                            return <button type='button' key={list.id} className='savedListButton' aria-label={`${list.name} list`} aria-pressed={selected}
                                disabled={!canManage} onClick={() => changeList(list, selected)}>
                                <div className='savedListButtonEmoji'>{list.name}{selected && <span className='savedListButtonCheckmark'><Check fontSize='inherit' /></span>}</div>
                            </button>;
                        })}
                        <NewSavedListButton disabled={!canManage} onClick={() => setShowEmoji(true)} />
                    </div>
                </div>
            </div>}
        </SwipeableDrawer>
        <EmojiPickerModal showEmojiPicker={showEmoji && Boolean(preview)} onClose={() => setShowEmoji(false)} onEmojiClick={emoji => {
            setShowEmoji(false); const existing = lists.find(list => list.name === emoji.emoji);
            changeList(existing || { name: emoji.emoji });
        }} />
    </>;
}
